import { TallestSlideModule } from "../modules/swiper/TallestSlideModule.js";
import { DistributedSlidesModule } from "../modules/swiper/DistributedSlidesModule.js";
import type { SwiperOptions } from "swiper/types";
import Swiper from "swiper";
import {
    Navigation,
    Pagination,
    Autoplay,
    A11y,
    Controller,
    EffectFade,
} from "swiper/modules";

/**
 * Helpers to assess the autoplay
 * behaviour based on a bitmask
 */
export enum AutoplayBehaviour {
    PauseOnInteraction = 1,
    PauseOnHover = 2,
    ReverseTheDirection = 4,
    StopAfterFirstIteration = 8,
}

export const pauseOnInteraction = (behaviour: number) =>
    (~~behaviour & AutoplayBehaviour.PauseOnInteraction) ===
    AutoplayBehaviour.PauseOnInteraction;

export const pauseOnHover = (behaviour: number) =>
    (~~behaviour & AutoplayBehaviour.PauseOnHover) ===
    AutoplayBehaviour.PauseOnHover;

export const reverseTheDirection = (behaviour: number) =>
    (~~behaviour & AutoplayBehaviour.ReverseTheDirection) ===
    AutoplayBehaviour.ReverseTheDirection;

export const stopAfterFirstIteration = (behaviour: number) =>
    (~~behaviour & AutoplayBehaviour.StopAfterFirstIteration) ===
    AutoplayBehaviour.StopAfterFirstIteration;

/**
 * Helpers to assess the animation
 * behaviour based on a bitmask
 */
export enum AnimationBehaviour {
    AnimateInGroups = 1,
    Loop = 2,
}

export const animateInGroups = (behaviour: number) =>
    (~~behaviour & AnimationBehaviour.AnimateInGroups) ===
    AnimationBehaviour.AnimateInGroups;

export const loop = (behaviour: number = 0) =>
    (~~behaviour & AnimationBehaviour.Loop) === AnimationBehaviour.Loop;

/**
 * Helpers to assess the navigation
 * options based on a bitmask
 */
export enum NavigationOption {
    ShowNavigation = 1,
    ShowPagination = 2,
}

export const showNavigation = (behaviour: number = 0) =>
    (~~behaviour & NavigationOption.ShowNavigation) ===
    NavigationOption.ShowNavigation;

export const showPagination = (behaviour: number = 0) =>
    (~~behaviour & NavigationOption.ShowPagination) ===
    NavigationOption.ShowPagination;

/**
 * Class to implement the slider functionality
 * for a single instance; the application is done
 * via the SliderManager class
 */
export class Slider {
    private instance: Swiper | null = null;

    private readonly keyboardHandler = (event: KeyboardEvent): void => {
        const instance = this.instance;
        if (
            !instance ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey ||
            event.shiftKey
        ) {
            return;
        }

        const isNext = event.key === "ArrowRight";
        const isPrevious = event.key === "ArrowLeft";
        if (!isNext && !isPrevious) return;

        const shouldAdvance = instance.rtlTranslate ? isPrevious : isNext;
        const canMove = shouldAdvance
            ? instance.allowSlideNext
            : instance.allowSlidePrev;
        if (!canMove) return;

        event.preventDefault();
        if (shouldAdvance) {
            instance.slideNext();
        } else {
            instance.slidePrev();
        }
    };

    /**
     * Constructor of the Slider
     *
     * @param {HTMLElement} slider
     */
    public constructor(private slider: HTMLElement) {
        this.instantiate();
    }

    public destroy(): void {
        this.slider.removeEventListener("keydown", this.keyboardHandler);
        this.instance?.destroy(true, true);
        this.instance = null;
    }

    /**
     * Instantiates the functionality of the slider
     *
     * @returns {void}
     * @throws {Error}
     */
    protected instantiate(): void {
        const swiper = this.slider.querySelector<HTMLElement>(".swiper");
        if (!swiper) throw new Error("Missing `swiper` class within slider");

        // Destructure the dataset-configuration
        const {
            autoplay = "0",
            autoplayBehaviour = "0",
            autoplayTimeout = "5000",

            animationBehaviour = "0",
            animationDuration = "300",
            animationTransition = "slide",

            navigationOptions = "",
            navigationPosition = "",
            navigationArrangement = "",
            navigationColoration = "",
            navigationReference = "",

            slides = "0",
            slidesMinWidth = "",
            slidesMaxWidth = "",
            slidesAutoWidth = "false",
            distributeSlides = "false",
            hidePartialSlides = "false",
            slidesSpaceBetween = "0",
            slidesOptimalWidth = "",
        } = this.slider.dataset;

        const optimalSlideWidth = Number.parseFloat(slidesOptimalWidth) || 0;
        const slideSpaceBetween = Number.parseFloat(slidesSpaceBetween) || 0;
        const useNarrowSingleSlideMode = () => {
            const canUseMode =
                slidesAutoWidth === "true" &&
                distributeSlides === "true" &&
                optimalSlideWidth > 0;

            // Reduce the visual size on tablet widths towards three to four
            // optimal slides before falling back to a single logo on phones. The
            // configured optimal width remains the target on wide containers.
            const singleSlideThreshold =
                optimalSlideWidth * 1.25 + slideSpaceBetween;
            const needsSingleSlide =
                canUseMode && swiper.clientWidth < singleSlideThreshold;
            const scale =
                canUseMode && !needsSingleSlide
                    ? Math.min(
                          1,
                          swiper.clientWidth /
                              (optimalSlideWidth * 3.5 + slideSpaceBetween * 2),
                      )
                    : 1;

            this.slider.classList.toggle(
                "slider-single-slide",
                needsSingleSlide,
            );
            this.slider.style.setProperty(
                "--slider-auto-width-scale",
                scale.toFixed(4),
            );
        };

        useNarrowSingleSlideMode();

        // Evaluated swiper options
        const options: Partial<SwiperOptions> = {
            modules: [TallestSlideModule, Controller, EffectFade],

            // Mainly to be able to
            // apply the bugfix at the end
            init: false,
            observer: true,
            observeParents: true,
            watchOverflow: true,
            // watchSlidesProgress: true,

            direction: "horizontal",
            loop: loop(~~animationBehaviour) && ~~slides > 1,
            speed: ~~animationDuration,
            // Do not move the slide for every tiny pointer movement. This is
            // particularly distracting for the fade effect after a long press.
            // The swipe is still evaluated and applied when it is released.
            followFinger: false,
            threshold: 8,
            // Fade overlays all slides, which is incompatible with the
            // simultaneously visible, individually sized slides used by the
            // distributed-slider mode.
            effect: distributeSlides === "true" ? "slide" : animationTransition,
            fadeEffect: {
                crossFade: true,
            },
            slidesPerView: slidesAutoWidth === "true" ? "auto" : 1,
            slidesPerGroup: 1,
            spaceBetween: slideSpaceBetween,
            breakpoints: {},
        };

        if (distributeSlides === "true") {
            (options.modules ??= []).push(DistributedSlidesModule);
        }

        // Breakpoints
        if (slidesAutoWidth !== "true" && slidesMinWidth) {
            options.breakpointsBase = "container";

            const minWidth = ~~slidesMinWidth;
            const gap = ~~slidesSpaceBetween;
            const slidesPerGroup = animateInGroups(~~animationBehaviour)
                ? null
                : 1;

            for (
                let slidesPerView = 1;
                slidesPerView <= ~~slides;
                slidesPerView++
            ) {
                const breakpoint =
                    slidesPerView * minWidth + (slidesPerView - 1) * gap;

                const showControls = ~~slides > slidesPerView;
                const breakpointOptions: SwiperOptions = {
                    slidesPerView,
                    slidesPerGroup: slidesPerGroup ?? slidesPerView,
                };

                if (showNavigation(~~navigationOptions)) {
                    breakpointOptions.navigation = {
                        enabled: showControls,
                    };
                }

                if (showPagination(~~navigationOptions)) {
                    breakpointOptions.pagination = {
                        enabled: showControls,
                    };
                }

                (options.breakpoints ??= {})[breakpoint] = breakpointOptions;
            }
        } else if (slidesAutoWidth !== "true" && slidesMaxWidth) {
            options.breakpointsBase = "container";
            const slidesPerGroup = animateInGroups(~~animationBehaviour)
                ? null
                : 1;

            let breakpoint = 0;
            let slidesPerView = 1;
            while (breakpoint < 1920) {
                breakpoint += ~~slidesMaxWidth;
                slidesPerView += 1;

                // We don't need to handle more slides
                // per view, than we have slides
                if (~~slides < slidesPerView) break;
                const showControls = ~~slides > slidesPerView;

                const breakpointOptions: SwiperOptions = {
                    slidesPerView,
                    slidesPerGroup: slidesPerGroup ?? slidesPerView,
                };

                if (showNavigation(~~navigationOptions)) {
                    breakpointOptions.navigation = {
                        enabled: showControls,
                    };
                }

                if (showPagination(~~navigationOptions)) {
                    breakpointOptions.pagination = {
                        enabled: showControls,
                    };
                }

                (options.breakpoints ??= {})[breakpoint] = breakpointOptions;
            }
        }

        // Navigation
        if (showNavigation(~~navigationOptions)) {
            (options.modules ??= []).push(Navigation);
            options.navigation = {
                enabled: true,
                hiddenClass: "opacity-0",
                disabledClass: "opacity-30 cursor-default",
                nextEl: this.slider.querySelector<HTMLElement>(
                    `[scope=navigation][template=right]`,
                ),
                prevEl: this.slider.querySelector<HTMLElement>(
                    `[scope=navigation][template=left]`,
                ),
            };
        }

        // Pagination
        if (showPagination(~~navigationOptions)) {
            (options.modules ??= []).push(Pagination);
            options.pagination = {
                enabled: true,
                el: this.slider.querySelector<HTMLElement>(
                    "[scope=indexation]",
                ),
                type: "bullets",
                clickable: true,
                dynamicBullets: true,
                dynamicMainBullets: 1,
                renderBullet: (index: number, className: string) => `<span
                    tabindex="0"
                    role="button"
                    aria-label="${window.translations["slider.goto"]?.replace(
                        "%s",
                        `${index + 1}`,
                    )}"
                    class="${className}"
                ></span>`,
            };
        }

        // Accessibility
        (options.modules ??= []).push(A11y);
        options.a11y = {
            enabled: true,
            prevSlideMessage: window.translations["slider.previous"],
            nextSlideMessage: window.translations["slider.next"],
            firstSlideMessage: window.translations["slider.first"],
            lastSlideMessage: window.translations["slider.last"],
            paginationBulletMessage: window.translations[
                "slider.goto"
            ]?.replace("%s", "{{index}}"),
        };

        // Autoplay
        if (!!~~autoplay) {
            (options.modules ??= []).push(Autoplay);
            options.autoplay = {
                delay: ~~autoplayTimeout,
                disableOnInteraction: pauseOnInteraction(~~autoplayBehaviour),
                pauseOnMouseEnter: pauseOnHover(~~autoplayBehaviour),
                reverseDirection: reverseTheDirection(~~autoplayBehaviour),
                stopOnLastSlide: stopAfterFirstIteration(~~autoplayBehaviour),
            };
        }

        // Initialize the slider
        const instance = new Swiper(swiper, options);

        if (optimalSlideWidth > 0) {
            instance.on("beforeResize", useNarrowSingleSlideMode);
        }

        if (hidePartialSlides === "true") {
            const synchronizePartialSlides = () => {
                const bounds = swiper.getBoundingClientRect();

                instance.slides.forEach((slide: HTMLElement) => {
                    const boundsSlide = slide.getBoundingClientRect();
                    const intersects =
                        boundsSlide.left < bounds.right &&
                        boundsSlide.right > bounds.left;
                    const fullyVisible =
                        boundsSlide.left >= bounds.left &&
                        boundsSlide.right <= bounds.right;

                    slide.classList.toggle(
                        "swiper-slide-partial",
                        intersects && !fullyVisible,
                    );
                });
            };

            instance.on("init", synchronizePartialSlides);
            instance.on("resize", synchronizePartialSlides);
            instance.on("update", synchronizePartialSlides);
            instance.on("setTranslate", synchronizePartialSlides);
        }

        // Keep navigation and pagination hidden when the current
        // breakpoint can display every available slide.
        const synchronizeControls = () => this.synchronizeControls(instance);
        instance.on("init", synchronizeControls);
        instance.on("update", synchronizeControls);
        instance.on("resize", synchronizeControls);
        instance.on("breakpoint", synchronizeControls);
        instance.on("lock", synchronizeControls);
        instance.on("unlock", synchronizeControls);

        // Link external navigation if configured
        if (navigationReference) {
            this.linkNavigation(instance, navigationReference);
        }

        // Always initialize because
        // options.init is false
        instance.init();
        this.instance = instance;

        // Keep keyboard navigation local to the slider containing the focused
        // control. Swiper's Keyboard module listens on `document`, which
        // causes every visible slider on a page to react to the same arrow key.
        this.slider.addEventListener("keydown", this.keyboardHandler);

        // Recalculate after images are available
        const images = Array.from(swiper.querySelectorAll("img"));

        Promise.allSettled(
            images.map((img) => {
                if (img.complete) return Promise.resolve();

                return (
                    img.decode?.().catch(() => undefined) ??
                    new Promise((resolve) => {
                        img.addEventListener("load", resolve, { once: true });
                        img.addEventListener("error", resolve, { once: true });
                    })
                );
            }),
        ).then(() => {
            instance.update();
            instance.emit("resize");
        });

        // Bug fix to be able to synchronize
        // a swiper instance within a popup
        if (~~slides > 1) {
            instance.snapGrid = [...instance.slidesGrid];
        }
    }

    /**
     * Synchronizes the rendered controls with Swiper's current overflow state.
     * Navigation and pagination are unnecessary when every slide is visible,
     * but a dedicated closer remains available when one is configured.
     *
     * @param {Swiper} instance
     * @returns {void}
     */
    protected synchronizeControls(instance: Swiper): void {
        const isLocked = instance.isLocked || instance.snapGrid.length <= 1;

        // Make a movable slider itself reachable by keyboard, in addition to
        // its individual controls. A locked slider has no keyboard action, so
        // it should not add an unnecessary tab stop.
        this.slider.setAttribute("tabindex", isLocked ? "-1" : "0");

        const controls = this.slider.querySelectorAll<HTMLElement>(
            '[scope="navigation"]:not([template="close"]), [scope="indexation"]',
        );

        controls.forEach((control) => {
            control.style.display = isLocked ? "none" : "";
            control.setAttribute("aria-hidden", isLocked ? "true" : "false");

            if (isLocked) {
                if (control.dataset.sliderTabindex === undefined) {
                    control.dataset.sliderTabindex =
                        control.getAttribute("tabindex") ?? "";
                }
                control.setAttribute("tabindex", "-1");
                return;
            }

            const originalTabindex = control.dataset.sliderTabindex;
            if (originalTabindex === undefined || originalTabindex === "") {
                control.removeAttribute("tabindex");
            } else {
                control.setAttribute("tabindex", originalTabindex);
            }
        });

        const controlsContainer =
            this.slider.querySelector<HTMLElement>(".controls");
        if (!controlsContainer) return;

        const hasVisibleControl = Array.from(
            controlsContainer.querySelectorAll<HTMLElement>(
                '[scope="navigation"], [scope="indexation"]',
            ),
        ).some((control) => control.style.display !== "none");

        controlsContainer.style.display = hasVisibleControl ? "" : "none";
    }

    /**
     * Links one or multiple external navigation lists to the slider
     *
     * @param {Swiper} instance
     * @param {string} navigationReference
     * @returns {void}
     */
    protected linkNavigation(
        instance: Swiper,
        navigationReference: string,
    ): void {
        const navigations = document.querySelectorAll<HTMLElement>(
            `[data-slider-navigation="${navigationReference}"]`,
        );
        if (!navigations.length) return;

        const allButtons: HTMLElement[] = [];

        navigations.forEach((navigation) => {
            const buttons = Array.from(
                navigation.querySelectorAll<HTMLElement>("[data-slide-to]"),
            );

            buttons.forEach((button) => {
                allButtons.push(button);

                button.addEventListener("click", (event) => {
                    event.preventDefault();

                    const slideTo = Number.parseInt(
                        button.dataset.slideTo ?? "",
                        10,
                    );
                    if (Number.isNaN(slideTo)) return;

                    if (instance.params.loop) {
                        instance.slideToLoop(slideTo);
                        return;
                    }

                    instance.slideTo(slideTo);
                });
            });
        });

        if (!allButtons.length) return;

        const updateActive = () => {
            const start = instance.realIndex ?? instance.activeIndex;

            // How many slides are visible
            let perView = instance.params.slidesPerView;

            // slidesPerView can be "auto"
            if (perView === "auto") {
                perView = instance.slides.filter((slide) =>
                    slide.classList.contains("swiper-slide-visible"),
                ).length;
            }

            const visibleCount = typeof perView === "number" ? perView : 1;
            const end = start + visibleCount - 1;

            allButtons.forEach((button, index) => {
                const active = index >= start && index <= end;

                button.setAttribute("aria-current", active ? "true" : "false");
            });
        };

        instance.on("init", updateActive);
        instance.on("slideChange", updateActive);
        instance.on("realIndexChange", updateActive);
    }
}
